import * as React from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { Form as FinalForm, FormRenderProps } from 'react-final-form';
import { Form, Tab } from 'semantic-ui-react';
import flatten from 'flat';

// @ts-ignore
import ErrorAlert from 'components/ErrorAlert';
// @ts-ignore
import FieldValue from 'components/FieldValue';
import arrayMutators from 'final-form-arrays';

import { AppState } from 'services/types';
import { selectIsNotificationsEnabled } from 'services/organizationConfig/selectors';
import { Person } from 'services/people/types';
import { resetErrorByNamespace } from 'services/error/actions';

import SubmittingSection from 'components/SubmissionSection';
import ProfileForm from './components/ProfileForm';
import CertificationsForm from './components/CertificationsForm';
import NotificationsForm from './components/NotificationsForm';
// Local Selectors
// @ts-ignore
import { makeSelectInitialValues } from './selectors';
import { PersonFormIntialValues, PersonFormValues } from './types';
import { formatPersonPayload } from './utils';

import {
    ProfileFieldName,
    CertificationsFieldName,
    NotificationsFieldName,
    TabName
} from './constants';

import './styles.scss';

const ProfileFieldNameArray = Object.values(ProfileFieldName);
const CertificationsFieldNameArray = Object.values(CertificationsFieldName);
const NotificationsFieldNameArray = Object.values(NotificationsFieldName);

// Constants
const NAMESPACE = ['PERSON_CREATE', 'PERSON_UPDATE'];

interface Pane {
    menuItem: TabName;
    pane: React.ReactElement;
}

interface OwnProps {
    personId?: number;
    onClose: (event: React.MouseEvent<HTMLButtonElement>) => void;
    onSubmit: (payload: Partial<Person>) => void;
}
interface ConnectedProps {
    initialValues: PersonFormIntialValues;
    isNotificationsEnabled: ReturnType<typeof selectIsNotificationsEnabled>;
}

interface DispatchProps {
    resetErrorByNamespace: typeof resetErrorByNamespace;
}

export type Props = OwnProps & ConnectedProps & DispatchProps;

export class PersonFormComponent extends React.PureComponent<Props> {
    componentWillUnmount(): void {
        const { resetErrorByNamespace: resetError } = this.props;

        resetError(NAMESPACE);
    }

    get panes(): Pane[] {
        const { personId, isNotificationsEnabled } = this.props;

        const allPanes: Pane[] = [
            {
                menuItem: TabName.PROFILE,
                pane: (
                    <Tab.Pane className="person-form-tab-pane" attached={false}>
                        <ProfileForm personId={personId} />
                    </Tab.Pane>
                )
            },
            {
                menuItem: TabName.CERTIFICATIONS,
                pane: (
                    <Tab.Pane className="person-form-tab-pane" attached={false}>
                        <CertificationsForm />
                    </Tab.Pane>
                )
            }
        ];

        if (isNotificationsEnabled) {
            allPanes.push({
                menuItem: TabName.NOTIFICATIONS,
                pane: (
                    <Tab.Pane className="person-form-tab-pane" attached={false}>
                        <NotificationsForm />
                    </Tab.Pane>
                )
            });
        }

        return allPanes;
    }

    handleSubmit = (values: PersonFormValues): void => {
        const { initialValues, onSubmit } = this.props;

        const payload = formatPersonPayload(initialValues, values);

        onSubmit(payload);
    };

    render(): React.ReactElement {
        const { onClose, initialValues } = this.props;
        const allPanes = this.panes;

        return (
            <FinalForm
                onSubmit={this.handleSubmit}
                initialValues={initialValues}
                subscription={{
                    submitFailed: true,
                    errors: true
                }}
                mutators={{
                    ...arrayMutators
                }}
                destroyOnUnregister
                render={(
                    props: FormRenderProps<PersonFormValues>
                ): React.ReactElement => {
                    const tabElement = React.useRef<any>(null);
                    const { errors, submitFailed, handleSubmit } = props;
                    const [lastSubmittedAt, setLastSubmittedAt] =
                        React.useState<Date | undefined>(undefined);

                    const onSubmit = (values: Record<string, any>) => {
                        handleSubmit(values);
                        setLastSubmittedAt(new Date());
                    };

                    React.useEffect(() => {
                        if (submitFailed && errors && tabElement.current) {
                            const { activeIndex } = tabElement.current.state;
                            const { panes } = tabElement.current.props;

                            // indexes by tabName e.g. { Profiles: 1 }
                            const tabNameIndexes = panes.reduce(
                                (
                                    result: Record<TabName, number>,
                                    pane: Pane,
                                    idx: number
                                ) => {
                                    const newResult = { ...result };

                                    newResult[pane.menuItem] = idx;

                                    return newResult;
                                },
                                {}
                            );

                            const minErrorIdx = Object.keys(
                                flatten(errors)
                            ).reduce(
                                (
                                    minIdx: number | undefined,
                                    fieldName: string
                                ) => {
                                    let idx;

                                    if (
                                        ProfileFieldNameArray.includes(
                                            fieldName as ProfileFieldName
                                        )
                                    ) {
                                        idx = tabNameIndexes[TabName.PROFILE];
                                    } else if (
                                        CertificationsFieldNameArray.includes(
                                            fieldName as CertificationsFieldName
                                        )
                                    ) {
                                        idx =
                                            tabNameIndexes[
                                                TabName.CERTIFICATIONS
                                            ];
                                    } else if (
                                        NotificationsFieldNameArray.includes(
                                            fieldName as NotificationsFieldName
                                        )
                                    ) {
                                        idx =
                                            tabNameIndexes[
                                                TabName.NOTIFICATIONS
                                            ];
                                    }

                                    if (idx === undefined) return minIdx;
                                    if (minIdx === undefined) return idx;

                                    return idx < minIdx ? idx : minIdx;
                                },
                                undefined
                            );

                            if (
                                minErrorIdx !== undefined &&
                                minErrorIdx !== activeIndex
                            ) {
                                tabElement.current.handleItemClick(null, {
                                    index: minErrorIdx
                                });
                            }
                        }
                    }, [lastSubmittedAt]);

                    return (
                        <Form
                            autoComplete="off"
                            onSubmit={onSubmit}
                            className="person-form"
                        >
                            <FieldValue name={ProfileFieldName.SPRAY_OPERATOR}>
                                {(isSprayOperator) => {
                                    const panes = isSprayOperator
                                        ? allPanes
                                        : allPanes.filter(
                                              ({ menuItem }) =>
                                                  menuItem !==
                                                  TabName.CERTIFICATIONS
                                          );

                                    return (
                                        <Tab
                                            ref={tabElement}
                                            panes={panes}
                                            menu={{
                                                secondary: true,
                                                pointing: true,
                                                widths: panes.length
                                            }}
                                            renderActiveOnly={false}
                                            className="person-form-tab"
                                        />
                                    );
                                }}
                            </FieldValue>
                            <ErrorAlert
                                style={{ marginBottom: '30px' }}
                                namespace={NAMESPACE}
                            />

                            <SubmittingSection
                                submittingButtonParam={{
                                    namespace: NAMESPACE
                                }}
                                normalCancelButtonParam={{ onClose }}
                            />
                        </Form>
                    );
                }}
            />
        );
    }
}

const mapStateToProps = createStructuredSelector<
    AppState,
    OwnProps,
    ConnectedProps
>({
    initialValues: makeSelectInitialValues(),
    isNotificationsEnabled: selectIsNotificationsEnabled
});

export default connect(mapStateToProps, {
    resetErrorByNamespace
})(PersonFormComponent);
